import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import LandingNavbar from '@/components/LandingNavbar';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ENTERPRISE_TIERS } from '@/config/marketingContent';
import { submitEnterpriseInquiry } from '@/services/indexService';

const EnterpriseAccess: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [seatEstimate, setSeatEstimate] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await submitEnterpriseInquiry({
        name: name.trim(),
        organization: organization.trim(),
        email: email.trim(),
        seat_estimate: seatEstimate.trim() || undefined,
        message: message.trim() || undefined,
      });
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit inquiry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black ni-grid-bg text-white">
      <LandingNavbar variant="dark" />
      <main className="pt-24 pb-16 max-w-6xl mx-auto px-4">
        <p className="ni-label mb-2">Procurement</p>
        <h1 className="text-2xl md:text-3xl font-sans font-bold mb-3">Enterprise access</h1>
        <p className="text-sm text-white/45 font-sans max-w-2xl mb-10">
          Institutional tiers with custom audit volume, mandate limits, and SAML/OIDC single sign-on.
          Contact our team for procurement and security review packages.
        </p>

        <div className="ni-panel overflow-hidden mb-10">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#2a2a2a]">
                <th className="ni-label px-4 py-3">Tier</th>
                <th className="ni-label px-4 py-3 hidden sm:table-cell">Seats</th>
                <th className="ni-label px-4 py-3 hidden md:table-cell">Mandates</th>
                <th className="ni-label px-4 py-3 hidden md:table-cell">Audits</th>
                <th className="ni-label px-4 py-3">Pricing</th>
                <th className="w-28" />
              </tr>
            </thead>
            <tbody>
              {ENTERPRISE_TIERS.map((tier) => (
                <tr
                  key={tier.id}
                  className={`border-b border-[#2a2a2a] last:border-0 ${'highlight' in tier && tier.highlight ? 'bg-white/[0.02]' : ''}`}
                >
                  <td className="px-4 py-4 font-sans font-medium">{tier.name}</td>
                  <td className="px-4 py-4 hidden sm:table-cell font-mono text-white/55">{tier.seats}</td>
                  <td className="px-4 py-4 hidden md:table-cell font-mono text-white/55">{tier.mandates}</td>
                  <td className="px-4 py-4 hidden md:table-cell font-mono text-white/55">{tier.audits}</td>
                  <td className="px-4 py-4 font-mono text-index-accent">{tier.price}</td>
                  <td className="px-4 py-4">
                    <Button
                      variant={'highlight' in tier && tier.highlight ? 'indexPrimary' : 'indexOutline'}
                      size="sm"
                      onClick={() => navigate(tier.path)}
                    >
                      {tier.cta}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="ni-panel p-6">
            <p className="ni-label mb-3">SSO &amp; security</p>
            <ul className="space-y-3 text-sm text-white/55 font-sans">
              <li>SAML 2.0 / OIDC — contact for IdP integration</li>
              <li>SOC 2 Type II aligned controls</li>
              <li>EU data residency (EU-WEST-4)</li>
              <li>Custom data retention and DPA available</li>
            </ul>
          </div>

          <div className="ni-panel p-6">
            <p className="ni-label mb-3">Procurement inquiry</p>
            {success ? (
              <div className="flex items-start gap-3 text-index-cleared">
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans font-medium text-white">Inquiry received</p>
                  <p className="text-sm text-white/45 mt-1">Our team will respond within 2 business days.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full rounded-sm border border-[#3a3a3a] bg-black px-3 py-2 text-sm text-white"
                />
                <input
                  required
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="Organization"
                  className="w-full rounded-sm border border-[#3a3a3a] bg-black px-3 py-2 text-sm text-white"
                />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Work email"
                  className="w-full rounded-sm border border-[#3a3a3a] bg-black px-3 py-2 text-sm text-white"
                />
                <input
                  value={seatEstimate}
                  onChange={(e) => setSeatEstimate(e.target.value)}
                  placeholder="Estimated seats (optional)"
                  className="w-full rounded-sm border border-[#3a3a3a] bg-black px-3 py-2 text-sm text-white"
                />
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Requirements (optional)"
                  rows={3}
                  className="w-full rounded-sm border border-[#3a3a3a] bg-black px-3 py-2 text-sm text-white resize-none"
                />
                <Button type="submit" variant="indexPrimary" disabled={saving} className="w-full">
                  {saving ? 'Submitting…' : 'Submit inquiry'}
                </Button>
              </form>
            )}
          </div>
        </div>

        <p className="mt-8 text-sm text-white/35 font-sans">
          Already have access?{' '}
          <Link to="/login" className="text-index-accent hover:underline">
            Sign in to workspace
          </Link>
        </p>
      </main>
      <MarketingFooter slim />
    </div>
  );
};

export default EnterpriseAccess;
